// @owners { team: patients-team }
import { useNavigation } from '@alto/navigation';
import { useCallback } from 'react';
// eslint-disable-next-line import/no-deprecated
import { fetchDeliveries } from '~shared/actions/deliveries';
// eslint-disable-next-line import/no-deprecated
import { type autoBillPrescriptionResult, autobillPrescription } from '~shared/actions/prescriptions';
import { useInvalidatePrescriptions } from '~shared/queries';
import { getEligiblePrescriptionIDs } from '~shared/selectors/progyny-wizard/getEligiblePrescriptionIDs';
import { useDispatchShared, useSelectorShared } from '~shared/store';

export const useCalculateProgynyOrderQuantities = () => {
  const dispatch = useDispatchShared();
  const { goBack, replace } = useNavigation();
  const medManagementPrescriptionIDs = useSelectorShared(getEligiblePrescriptionIDs);
  const { invalidatePrescriptionsQuery } = useInvalidatePrescriptions();

  // The correct order is to refresh deliveries after autobill, before refreshing prescriptions.
  // Why? Because refreshing prescriptions includes logic to remove any prescriptions from cart that aren't
  // ready to schedule. (see useRemoveUnschedulablePrescriptionsFromCart).
  // A delivery will appear not ready to schedule if it's refreshed during autobill and not after.
  const calculateQuantities = useCallback(async () => {
    const autobillResults = await Promise.all(
      medManagementPrescriptionIDs.map((id: number) => {
        // eslint-disable-next-line import/no-deprecated
        return dispatch(autobillPrescription(id)).then((result: autoBillPrescriptionResult) => result.success);
      }),
    );

    if (autobillResults.some((success) => !success)) {
      goBack();
      return;
    }

    // eslint-disable-next-line import/no-deprecated
    const fetchDeliveriesSuccess = await dispatch(fetchDeliveries());

    invalidatePrescriptionsQuery();

    if (!fetchDeliveriesSuccess) {
      goBack();
      return;
    }

    // Instead of navigating as one would normally do, we use replace to remove this route from the stack so that going
    // backwards in future screens will skip this screen. Unfortunately, we lose the transition animation if we do this.
    // Also, by specifying the key, if the patient navigates backwards before the requests finish, this call to replace
    // will do nothing since it will only apply to that key, which no longer exists.
    // 🚀 We also know that we're routing to `PROGYNY_WIZARD_QUANTITY_REVIEW` from this screen.
    replace('RouteProgynyWizardQuantityReview');
  }, [dispatch, goBack, invalidatePrescriptionsQuery, medManagementPrescriptionIDs, replace]);

  return { calculateQuantities };
};
